import {useCallback, useEffect, useRef} from 'react';
import {useScript} from '@huse/script';
import {callHandlers} from '@baidu/health-tool-box-bridge';

import {Toast, Modal} from '@baidu/hcui';
import {updateVote} from '@/services/vote.service';
import * as Logger from '@/utils/ubcUtils';

import VoteCard from './card';
import type {CardProps} from './card';
import useParis from './useParis';
import styles from './cardList.less';

interface CardListProps {
    cards: Array<Omit<CardProps, 'status' | 'index' | 'onVote'>>;
    status: 1 | 2 | 3;
    moduleId: number;
    query: Record<string, unknown>;
    getVoteInfo?: () => void;
    voteType: string;
}

declare global {
    interface Window {
        PassMachine: {
            mkd: any;
        };
    }
}

function CardList(props: CardListProps) {
    const {
        cards,
        status,
        moduleId,
        query,
        getVoteInfo,
        voteType,
    } = props;

    const passMkd = useRef<{
        initVcode: () => void;
        removeMask: () => void;
    }>();
    const currentItemId = useRef<number>();
    const voteSuccess = useRef<any>();
    const [errorInfo, sign, parisInstance] = useParis();

    const [scriptloded] = useScript('https://wappass.baidu.com/static/machine/js/api/mkd.js');

    const handleErrorProcess = useCallback(
        (error: any) => {
            switch (error.responseStatus) {
                // h5 票数用完
                case 2230:
                    Modal.confirm({
                        content: error.message,
                        cancelText: '取消',
                        okText: '打开',
                        onOk: () => {
                            Logger.clk({
                                page: 'vote_index',
                                value: 'no_vote_win_open',
                                ext: {
                                    // eslint-disable-next-line camelcase
                                    sf_ref: query.channel,
                                    id: query.id,
                                },
                            });
                            window.location.href = 'https://drs.baidu.com/toolm/cloud-notes/simpleDownload';
                        },
                        onCancel: () => {
                            Logger.clk({
                                page: 'vote_index',
                                value: 'no_vote_win_cancel',
                                ext: {
                                    // eslint-disable-next-line camelcase
                                    sf_ref: query.channel,
                                    id: query.id,
                                },
                            });
                        },
                    });
                    Logger.view({
                        page: 'vote_index',
                        value: 'no_vote_window',
                        ext: {
                            // eslint-disable-next-line camelcase
                            sf_ref: query.channel,
                            id: query.id,
                        },
                    });
                    break;
                // app 票数用完
                case 2231:
                    Toast.show(error.message);
                    Logger.view({
                        page: 'vote_index',
                        value: 'no_vote_window',
                        ext: {
                            // eslint-disable-next-line camelcase
                            sf_ref: query.channel,
                            id: query.id,
                        },
                    });
                    break;

                // 验证码输入
                case 2232:
                    passMkd?.current?.initVcode();
                    break;
                case 2234:
                    Modal.confirm({
                        content: error.message,
                        cancelText: '取消',
                        okText: '去认证',
                        onOk: async () => {
                            const res = await callHandlers.identity.loadAccountRealName();
                            if (res?.code === 0) {
                                window.location.href = error?.responseData?.url;
                            }
                        },
                    });
                    break;
                // 未认证
                case 3:
                    Modal.confirm({
                        content: error.message,
                        cancelText: '取消',
                        okText: '去认证',
                        onOk: () => {
                            window.location.href = error?.responseData?.url;
                        },
                    });
                    break;
                default:
                    Toast.show(error.message);
            }
        },
        [query.channel, query.id]
    );

    const fetchUpdateVote = useCallback(
        // eslint-disable-next-line complexity
        async (captcha?: any, callback?: (status: 'success' | 'error') => void) => {
            voteSuccess.current = callback;
            try {
                try {
                    await parisInstance?.getAbcliteAutoResponse();
                } catch (error) {
                    console.error(error);
                }

                const res = await updateVote({
                    id: query.id as string,
                    moduleId: moduleId,
                    itemId: currentItemId.current as number,
                    fieldValue: query as any,
                    captcha,
                }, {
                    headers: {
                        'Acs-Token': sign || errorInfo,
                    },
                });

                if (res && res.code) {
                    const errorToast = `${voteType === 'vote' ? '投票' : '点赞'}失败了～`;
                    Toast.show(errorToast);
                    return;
                }
                voteSuccess.current && voteSuccess.current('success');
                getVoteInfo && getVoteInfo();
                const successToast = `${voteType === 'vote' ? '投票' : '点赞'}成功，感谢您的支持`;
                Toast.show(successToast);
            } catch (error: any) {
                handleErrorProcess(error);
                voteSuccess.current && voteSuccess.current('error');
            }
        },
        [errorInfo, getVoteInfo, handleErrorProcess, moduleId, query, sign]
    );

    useEffect(
        () => {
            if (scriptloded && window.PassMachine && window.PassMachine.mkd && !passMkd.current) {
                const PassMkd = window.PassMachine.mkd;
                passMkd.current = new PassMkd({
                    // **自定义验证码类型**
                    type: 'spin', // spin:旋转（语意）验证, slide: 滑块验证
                    // 渲染位置 （内嵌模式） （弹层模式不需要id）
                    // id: 'passMkd',
                    // 产品线接入ak
                    ak: '7e3220714f4739db259fe0ad43f5ae20',
                    testurl: false,
                    maskModule: true,
                    // 验证结果的回调函数
                    verifySuccessFn(data: any) {
                        // 可以拿到ds 和 tk 去问反作弊是否是人机。
                        passMkd.current?.removeMask();
                        fetchUpdateVote(data, voteSuccess.current);
                    },
                });
            }
        },
        [fetchUpdateVote, scriptloded]
    );

    const handleVote = useCallback(
        async (itemId: number, callback: (status: 'success' | 'error') => void) => {
            currentItemId.current = itemId;
            fetchUpdateVote(null, callback);
        },
        [fetchUpdateVote]
    );

    return (
        <div className={styles.cardList}>
            {
                cards?.map(card => (
                    <div key={card.itemId} className={styles.cardWrap}>
                        <VoteCard
                            {...card}
                            itemId={card.itemId}
                            status={status}
                            voteType={voteType}
                            onVote={handleVote}
                        />
                    </div>
                ))
            }
        </div>
    );
}

export default CardList;
